(ns io.daskul.webapp.widgets.dashboard.enrollment-movement
  "Enrollment Movement widget (#729) — New + Renewal stacked above the zero line,
   Withdrawal below, and a Net line on top, so drop-off is visible and the eye
   reads NET growth rather than gross adds.

   Expects a metric map with :widget :enrollment-movement shape:
     {:widget :enrollment-movement
      :label  \"Enrollment Movement\"
      :series [{:key :new :label \"New\" :color \"violet\"} …]
      :data   [{:period \"2026-08\" :new 14 :renewal 13 :withdrawal 8 :net 19} …]}"
  (:require
   [clojure.string :as str]
   [uix.core :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]))

(defn- color-var [colors k]
  (if-let [c (get colors k)] (str "var(--mantine-color-" c "-6)") "var(--text)"))

(defn- nice-step
  "Smallest 'nice' (1/2/5 × 10ⁿ) increment that splits m into ~3 intervals;
   never below 1 (counts are whole numbers)."
  [m]
  (if (<= m 0)
    1
    (let [raw  (/ m 3)
          mag  (Math/pow 10 (Math/floor (/ (Math/log raw) (Math/log 10))))
          norm (/ raw mag)
          nice (cond (<= norm 1) 1 (<= norm 2) 2 (<= norm 5) 5 :else 10)]
      (max 1 (int (Math/round (* nice mag)))))))

(defui enrollment-movement
  [{:keys [label description data series]}]
  (let [data   (vec data)
        ;; series :key arrives from JSON as a string ("new"); the bars/net look
        ;; colours up with keyword literals (:new). Key by keyword so both sides
        ;; agree — otherwise every lookup misses and falls back to var(--text)
        ;; (near-black), painting the bars grey instead of their series colour.
        colors (into {} (map (juxt (comp keyword :key) :color)) series)
        n      (count data)
        ;; window totals per series — shown in the legend alongside the Y axis,
        ;; so the reader gets both the per-month scale and the 6-month totals.
        totals (reduce (fn [a d] (-> a (update :new + (or (:new d) 0))
                                       (update :renewal + (or (:renewal d) 0))
                                       (update :withdrawal + (or (:withdrawal d) 0))
                                       (update :net + (or (:net d) 0))))
                       {:new 0 :renewal 0 :withdrawal 0 :net 0} data)]
    ($ ui/paper {:p "lg" :radius "md" :withBorder true}
      ($ ui/group {:gap 6 :align "center" :mb "sm" :wrap "nowrap"}
        ($ ui/text {:size "sm" :fw 700} label)
        (when description
          ($ ui/tooltip {:label description :multiline true :w 260
                         :position "top-end" :withArrow true}
            ($ ui/text {:c "dimmed" :style #js {:cursor "help"} :lh 1}
              ($ icons/icon-info-circle {:size 14 :stroke 1.5})))))
      (if (zero? n)
        ($ ui/text {:size "sm" :c "dimmed"} "No enrollment activity in this period.")
        ;; Wide aspect (720×160) so at width:100% the chart stays ~260px tall on
        ;; a wide container instead of ballooning to ~340px+.
        (let [W 720 H 160 top 10 plot-bottom 128
              plot-left 36                        ; left gutter for Y-axis count labels
              plot-w   (- W plot-left)
              plot-h   (- plot-bottom top)
              raw-up   (reduce max 0 (map #(+ (:new %) (:renewal %)) data))
              raw-down (reduce max 0 (map :withdrawal data))
              ;; round each side up to a whole number of "nice" steps so the top
              ;; gridline is a round count and the tallest bar sits just under it.
              step     (nice-step (max raw-up raw-down))
              up-max   (let [u (* step (int (Math/ceil (/ raw-up step))))]
                         (if (and (zero? u) (zero? raw-down)) step u))
              down-max (* step (int (Math/ceil (/ raw-down step))))
              total    (max 1 (+ up-max down-max))
              scale    (/ plot-h total)
              zero-y   (+ top (* up-max scale))
              xstep    (/ plot-w n)
              barw     (min 44 (* xstep 0.5))
              cx       (fn [i] (+ plot-left (* xstep i) (/ xstep 2)))
              ty       (fn [v] (- zero-y (* v scale)))   ; count → y (up for +v)
              net-pts  (str/join " " (map-indexed
                                       (fn [i d] (str (cx i) "," (ty (:net d))))
                                       data))]
          ($ :svg {:viewBox (str "0 0 " W " " H) :role "img"
                   :style #js {:width "100%" :height "auto"}}
            ;; Y axis — faint gridlines at nice counts, right-aligned labels in the
            ;; left gutter. Positive counts above the zero line, withdrawals below.
            ($ :g {}
              (for [t (range 0 (inc up-max) step)]
                (let [y (ty t)]
                  ($ :g {:key (str "gu" t)}
                    (when (pos? t)          ; the 0 gridline is the zero baseline below
                      ($ :line {:x1 plot-left :y1 y :x2 W :y2 y
                                :stroke "var(--mantine-color-gray-3)" :strokeWidth 1}))
                    ($ :text {:x (- plot-left 6) :y (+ y 3) :fontSize 10 :textAnchor "end"
                              :fill "var(--mantine-color-gray-6)"} (str t)))))
              (for [t (range step (inc down-max) step)]
                (let [y (+ zero-y (* t scale))]
                  ($ :g {:key (str "gd" t)}
                    ($ :line {:x1 plot-left :y1 y :x2 W :y2 y
                              :stroke "var(--mantine-color-gray-3)" :strokeWidth 1})
                    ($ :text {:x (- plot-left 6) :y (+ y 3) :fontSize 10 :textAnchor "end"
                              :fill "var(--mantine-color-gray-6)"} (str t))))))
            ;; stacked up-bars (renewal bottom, new on top) + down-bar (withdrawal)
            (for [[i d] (map-indexed vector data)]
              (let [x     (- (cx i) (/ barw 2))
                    ren-h (* (:renewal d) scale)
                    new-h (* (:new d) scale)
                    wd-h  (* (:withdrawal d) scale)]
                ($ :g {:key i}
                  ($ :rect {:x x :y (- zero-y ren-h) :width barw :height ren-h
                            :fill (color-var colors :renewal) :opacity 0.85})
                  ($ :rect {:x x :y (- zero-y ren-h new-h) :width barw :height new-h
                            :fill (color-var colors :new) :opacity 0.9})
                  ($ :rect {:x x :y zero-y :width barw :height wd-h
                            :fill (color-var colors :withdrawal) :opacity 0.8})
                  ($ :text {:x (cx i) :y 146 :fontSize 10 :textAnchor "middle"
                            :fill "var(--mantine-color-gray-6)"} (:period d)))))
            ;; zero baseline + net line
            ($ :line {:x1 plot-left :y1 zero-y :x2 W :y2 zero-y
                      :stroke "var(--mantine-color-gray-5)" :strokeWidth 1.2})
            ($ :polyline {:points net-pts :fill "none"
                          :stroke (color-var colors :net) :strokeWidth 2.2})
            ;; per-month hover targets — a transparent full-height column tooltipped
            ;; with that month's own breakdown (the legend only shows 6-month totals).
            (for [[i d] (map-indexed vector data)]
              ($ ui/tooltip {:key (str "hit" i)
                             :label (str (:period d) " · New " (:new d) " · Renewal " (:renewal d)
                                         " · Withdrawal " (:withdrawal d) " · Net "
                                         (let [nv (:net d)] (str (when (pos? nv) "+") nv)))
                             :withArrow true :position "top"}
                ($ :rect {:x (- (cx i) (/ xstep 2)) :y 0 :width xstep :height plot-bottom
                          :fill "transparent"
                          :style #js {:pointerEvents "all" :cursor "help"}})))))
        )
      ;; legend — carries each series' window total (the 6-month magnitude);
      ;; Net gets an explicit sign, e.g. "Net +19".
      ($ ui/group {:gap "md" :mt "sm" :wrap "wrap"}
        (for [{:keys [key label]} series]
          (let [k (keyword key)
                v (get totals k)]
            ($ ui/group {:key (name k) :gap 6 :align "center" :wrap "nowrap"}
              ($ ui/box {:style {:width 9 :height 9 :borderRadius 3
                                 :background (color-var colors k)}})
              ($ ui/text {:size "xs" :c "dimmed"} label)
              ($ ui/text {:size "xs" :fw 700
                          :style {:fontFamily "Sora, sans-serif" :fontVariantNumeric "tabular-nums"}}
                (if (= k :net) (str (when (pos? v) "+") v) v)))))))))
